import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule, UrlSerializer } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { AsteriskModule } from '@salesrun/ngx-asterisk';
import { BlockingLoaderModule, BlockingLoaderService } from '@salesrun/ngx-blocking-loader';
import { ERROR_TRANSFORMS, LOGGING_ERROR_HANDLERS, LoggingModule } from '@salesrun/ngx-logging';
import { UnauthenticatedError } from '@salesrun/ngx-logging/errors';
import { IErrorHandler } from '@salesrun/ngx-logging/models/IErrorHandler';
import { IErrorTransform } from '@salesrun/ngx-logging/models/IErrorTransform';
import { LongTooltipModule } from '@salesrun/ngx-long-tooltip';
import { ReactiveInputModule } from '@salesrun/ngx-reactive-input';
import { TimezoneModule } from '@salesrun/ngx-timezone';
import { DialogModule } from '../../projects/packages/src/ngx-dialog/srp-dialog.module';
import { appConfig } from '../app/app.config.mjs';
import { environment } from '../environments/environment';
import { ApiService } from './api/api.service';
import { IISCompatibleUrlSerializer } from './api/iis-compatible.url-serializer';
import { PageButtonsComponent } from './components/page-buttons/page-buttons.component';
import { AutocompleteComponent } from './controls/qb-autocomplete/qb-autocomplete.component';
import { SelectAllModeComponent } from './controls/qb-select-all-mode/qb-select-all-mode.component';
import { SelectAllComponent } from './controls/qb-select-all/qb-select-all.component';
import { Error404Component } from './error/error-404/error-404.component';
import { Error500Component } from './error/error-500/error-500.component';
import { TimeoutPauseService } from './expiration/timeout-pause.service';
import { TimeoutService } from './expiration/timeout.service';
import { PhonePipe } from './pipes/phone.pipe';
import { AssetsService } from './services/assets.service';
import { JwtStorageService } from './services/jwt-storage.service';
import { jwtOptionsFactory, JwtService } from './services/jwt.service';


const components = [
    // controls
    SelectAllComponent,
    AutocompleteComponent,
    SelectAllModeComponent,
    PageButtonsComponent,
    // errors
    Error404Component,
    Error500Component,
];

const pipes = [
    PhonePipe,
];

@NgModule({
    imports: [
        // @ET: no export, modulues should be imported in each module manually to reduce coupling
        // these modules imported by Shared because some of Shared components still not separated into own modules,
        // but still require some deps
        // external
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatAutocompleteModule,
        MatTooltipModule,
        FlexLayoutModule,
        MatDividerModule,
        MatIconModule,
        // salesrun packages
        ReactiveInputModule,
        LongTooltipModule,
        AsteriskModule,
    ],
    declarations: [
        ...components,
        ...pipes,
    ],
    exports: [
        ...components,
        ...pipes,
    ],
})
export class SharedBeetweenAppsModule {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static forRoot(): ModuleWithProviders<any>[] {
        return [
            {
                ngModule: SharedBeetweenAppsModule,
                providers: [
                    ...pipes,
                    // material
                    MatIconRegistry,
                    // custom
                    AssetsService,
                    JwtStorageService,
                    JwtService,
                    ApiService,
                    TimeoutPauseService,
                    TimeoutService,
                    { provide: UrlSerializer, useClass: IISCompatibleUrlSerializer },
                    { provide: LOGGING_ERROR_HANDLERS, multi: true, useValue: unauthenticatedErrorHandler },
                    { provide: ERROR_TRANSFORMS, multi: true, useFactory: closeLoaderOnError, deps: [BlockingLoaderService] },
                ],
            },
            JwtModule.forRoot({
                jwtOptionsProvider: {
                    provide: JWT_OPTIONS,
                    useFactory: jwtOptionsFactory,
                    deps: [JwtStorageService],
                },
            }),
            DialogModule.forRoot(),
            TimezoneModule.forRoot(),
            LoggingModule.forRoot({
                ...(appConfig.logging ?? { enableSending: false }),
                production: environment.production,
            }),
            BlockingLoaderModule.forRoot(),
        ];
    }
}

const unauthenticatedErrorHandler: IErrorHandler<UnauthenticatedError> = {
    type: UnauthenticatedError,
    handle: () => {
        if (!window.location.pathname.startsWith('/login')) {
            location.href = '/login';
        }
    },
};

const closeLoaderOnError = (progress: BlockingLoaderService): IErrorTransform => err => {
    progress.hide();
    return err;
};
